import { Box, IconButton, Stack, Tooltip } from "@mui/material";
import { CreateAppendAnythingModule } from "bpmn-js-create-append-anything";
import Modeler from "bpmn-js/lib/Modeler";
import {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useRef,
} from "react";
import { useTranslation } from "react-i18next";
import useNotification from "component/global/event-handler/hooks/use-notification";
import { FLUSS_BPMN_LABEL_KEYS } from "modules/fluss/utils/bpmnLabelTranslations";
import { ensureFlussDiagram, flussModdleDescriptor, FLUSS_DESIGN_NODE_TYPES, FLUSS_NODE_TYPES } from "modules/fluss/utils/bpmn";
import { flussPopupRankModule, flussContextPadModule, setFlussPopupScriptPermission } from "modules/fluss/utils/flussPopupEntries";
import { flussCallActivityOverlayModule, setFlussCallActivityOpenHandler, setFlussCallActivityOverlayTranslate } from "modules/fluss/utils/flussCallActivityOverlay";
import { flussSubProcessToggleModule, setFlussSubProcessToggleTranslate } from "modules/fluss/utils/flussSubProcessToggle";
import { FlussElementSelection } from "modules/fluss/types/fluss";

import "bpmn-js/dist/assets/diagram-js.css";
import "bpmn-js/dist/assets/bpmn-font/css/bpmn.css";
import "modules/fluss/styles/fluss-studio.scss";
import { flussElementReplaceModule } from "modules/fluss/utils/flussElementReplace";

// RestrictedPaletteProvider is a plain bpmn-js/diagram-js class instantiated via dependency
// injection, not a React component, so it can't call the useTranslation() hook directly. The
// FlussCanvas component below keeps this module-level reference in sync with the live
// react-i18next translator on every render (including language changes).
type FlussCanvasTranslator = (key: string, options?: { defaultValue?: string }) => string;
let flussCanvasTranslate: FlussCanvasTranslator = (key, options) => options?.defaultValue ?? key;

// Lote 1 (AJUSTE 003a, plano §2.3): unlike RestrictedPaletteProvider above (which we author and
// can call flussCanvasTranslate with an explicit fluss.* key), bpmn-js-create-append-anything and
// bpmn-js's own context-pad/replace-menu live in node_modules and call the *injected* diagram-js
// `translate` service with a hardcoded English string as both the message and the lookup key
// (e.g. translate('User task')). This bridges that service to react-i18next via
// FLUSS_BPMN_LABEL_KEYS: known strings resolve to a fluss.* key; anything missing from the
// dictionary falls back to the raw English text untouched (never a blank/broken label).
type DiagramJsTranslate = (template: string, replacements?: Record<string, unknown>) => string;
let flussBpmnTranslate: DiagramJsTranslate = (template) => template;

const flussTranslateModule = {
    translate: [ "value", (template: string, replacements?: Record<string, unknown>) =>
        flussBpmnTranslate(template, replacements) ],
};

const createPaletteAction = (
    create: any,
    elementFactory: any,
    type: string,
    group: string,
    className: string,
    titleKey: string,
    defaultValue: string
) => ({
    group,
    className,
    title: flussCanvasTranslate(titleKey, { defaultValue }),
    action: {
        dragstart: (event: Event) => create.start(event, elementFactory.createShape({ type })),
        click: (event: Event) => create.start(event, elementFactory.createShape({ type })),
    },
});

// bpmn-js's own createSubprocess/createParticipant (features/palette/PaletteProvider.js) need the
// same two-step (subprocess+child start event, or elementFactory.createParticipantShape()) — a
// plain createShape({type}) does not produce a valid collapsed/empty pool or sub-process.
const createSubprocessAction = (create: any, elementFactory: any) => (event: Event) => {
    const subProcess = elementFactory.createShape({ type: FLUSS_DESIGN_NODE_TYPES.subProcess, isExpanded: true });
    const startEvent = elementFactory.createShape({ type: FLUSS_NODE_TYPES.startEvent, parent: subProcess });
    create.start(event, [ subProcess, startEvent ], { hints: { autoSelect: [ subProcess ] } });
};

const createCollapsedSubprocessAction = (create: any, elementFactory: any) => (event: Event) => {
    create.start(event, elementFactory.createShape({ type: FLUSS_DESIGN_NODE_TYPES.subProcess, isExpanded: false }));
};

const createParticipantAction = (create: any, elementFactory: any) => (event: Event) => {
    create.start(event, elementFactory.createParticipantShape());
};

function RestrictedPaletteProvider(
    this: any,
    palette: any,
    create: any,
    elementFactory: any,
    globalConnect: any,
    handTool: any,
    lassoTool: any,
    spaceTool: any
) {
    palette.registerProvider(this);

    this.getPaletteEntries = () => ({
        "hand-tool": {
            group: "tools",
            className: "bpmn-icon-hand-tool",
            title: flussCanvasTranslate("fluss.canvas.hand_tool", { defaultValue: "Activate the hand tool" }),
            action: { click: (event: Event) => handTool.activateHand(event) },
        },
        "lasso-tool": {
            group: "tools",
            className: "bpmn-icon-lasso-tool",
            title: flussCanvasTranslate("fluss.canvas.lasso_tool", { defaultValue: "Activate the lasso tool" }),
            action: { click: (event: Event) => lassoTool.activateSelection(event) },
        },
        "space-tool": {
            group: "tools",
            className: "bpmn-icon-space-tool",
            title: flussCanvasTranslate("fluss.canvas.space_tool", { defaultValue: "Activate the create/remove space tool" }),
            action: { click: (event: Event) => spaceTool.activateSelection(event) },
        },
        "global-connect-tool": {
            group: "tools",
            className: "bpmn-icon-connection-multi",
            title: flussCanvasTranslate("fluss.canvas.global_connect_tool", { defaultValue: "Activate the global connect tool" }),
            action: { click: (event: Event) => globalConnect.start(event) },
        },
        "create.start-event": createPaletteAction(
            create, elementFactory, FLUSS_NODE_TYPES.startEvent, "event", "bpmn-icon-start-event-none",
            "fluss.canvas.create_start_event", "Create start event"
        ),
        "create.end-event": createPaletteAction(
            create, elementFactory, FLUSS_NODE_TYPES.endEvent, "event", "bpmn-icon-end-event-none",
            "fluss.canvas.create_end_event", "Create end event"
        ),
        "create.user-task": createPaletteAction(
            create, elementFactory, FLUSS_NODE_TYPES.userTask, "activity", "bpmn-icon-user-task",
            "fluss.canvas.create_user_task", "Create user task"
        ),
        "create.service-task": createPaletteAction(
            create, elementFactory, FLUSS_NODE_TYPES.serviceTask, "activity", "bpmn-icon-service-task",
            "fluss.canvas.create_service_task", "Create service task"
        ),
        "create.send-task": createPaletteAction(
            create, elementFactory, FLUSS_NODE_TYPES.sendTask, "activity", "bpmn-icon-send-task",
            "fluss.canvas.create_send_task", "Create send task"
        ),
        "create.exclusive-gateway": createPaletteAction(
            create, elementFactory, FLUSS_NODE_TYPES.exclusiveGateway, "gateway", "bpmn-icon-gateway-xor",
            "fluss.canvas.create_exclusive_gateway", "Create exclusive gateway"
        ),
        "create.parallel-gateway": createPaletteAction(
            create, elementFactory, FLUSS_NODE_TYPES.parallelGateway, "gateway", "bpmn-icon-gateway-parallel",
            "fluss.canvas.create_parallel_gateway", "Create parallel gateway"
        ),
        "create.inclusive-gateway": createPaletteAction(
            create, elementFactory, FLUSS_NODE_TYPES.inclusiveGateway, "gateway", "bpmn-icon-gateway-or",
            "fluss.canvas.create_inclusive_gateway", "Create inclusive gateway"
        ),
        // Lote 1 (AJUSTE 003a, plano §2.3, revisado): a partir daqui a paleta fixa espelha o
        // PaletteProvider padrão do bpmn-js (14 itens, igual ao demo.bpmn.io) — os atalhos acima
        // continuam com atalho de um clique por serem tipos que Fluss::Compiler::SUPPORTED executa;
        // todo o resto do catálogo BPMN (subtipos de tarefa/evento, etc.) fica no popup "Create element" do
        // bpmn-js-create-append-anything (registrado abaixo), não em botões dedicados aqui.
        "create.task": createPaletteAction(
            create, elementFactory, FLUSS_DESIGN_NODE_TYPES.task, "activity", "bpmn-icon-task",
            "fluss.canvas.create_task", "Create task"
        ),
        "create.subprocess-expanded": {
            group: "activity",
            className: "bpmn-icon-subprocess-expanded",
            title: flussCanvasTranslate("fluss.canvas.create_subprocess", { defaultValue: "Create expanded sub-process" }),
            action: {
                dragstart: createSubprocessAction(create, elementFactory),
                click: createSubprocessAction(create, elementFactory),
            },
        },
        "create.subprocess-collapsed": {
            group: "activity",
            className: "bpmn-icon-subprocess-collapsed",
            title: flussCanvasTranslate("fluss.canvas.create_subprocess_collapsed", { defaultValue: "Create collapsed sub-process" }),
            action: {
                dragstart: createCollapsedSubprocessAction(create, elementFactory),
                click: createCollapsedSubprocessAction(create, elementFactory),
            },
        },
        "create.data-object": createPaletteAction(
            create, elementFactory, FLUSS_DESIGN_NODE_TYPES.dataObjectReference, "data-object", "bpmn-icon-data-object",
            "fluss.canvas.create_data_object", "Create data object reference"
        ),
        "create.data-store": createPaletteAction(
            create, elementFactory, FLUSS_DESIGN_NODE_TYPES.dataStoreReference, "data-store", "bpmn-icon-data-store",
            "fluss.canvas.create_data_store", "Create data store reference"
        ),
        "create.participant-expanded": {
            group: "collaboration",
            className: "bpmn-icon-participant",
            title: flussCanvasTranslate("fluss.canvas.create_participant", { defaultValue: "Create pool/participant" }),
            action: {
                dragstart: createParticipantAction(create, elementFactory),
                click: createParticipantAction(create, elementFactory),
            },
        },
        "create.group": createPaletteAction(
            create, elementFactory, FLUSS_DESIGN_NODE_TYPES.group, "artifact", "bpmn-icon-group",
            "fluss.canvas.create_group", "Create group"
        ),
    });
}

(RestrictedPaletteProvider as any).$inject = [
    "palette",
    "create",
    "elementFactory",
    "globalConnect",
    "handTool",
    "lassoTool",
    "spaceTool",
];

export const restrictedPaletteModule = {
    __init__: ["paletteProvider"],
    paletteProvider: ["type", RestrictedPaletteProvider],
};

export interface FlussCanvasHandle {
    saveXml: () => Promise<string>;
    undo: () => void;
    redo: () => void;
    focusElement: (elementId: string) => void;
    clearMarkers: () => void;
    getModeler: () => any;
}

const safeRemoveMarker = (canvas: any, registry: any, id: string, marker: string) => {
    if (registry?.get(id)) canvas?.removeMarker(id, marker);
};

// The palette sits over the canvas rather than taking space in its layout. After fitting the
// diagram, move the viewport only when an opening Start is actually behind that palette.
const revealStartBehindPalette = (modeler: any, container: HTMLDivElement) => {
    const palette = container.querySelector(".djs-palette");
    if (!palette) return;
    const containerRect = container.getBoundingClientRect();
    const paletteRect = palette.getBoundingClientRect();
    const paletteRight = paletteRect.right - containerRect.left;
    if (paletteRight <= 0) return;

    const canvas = modeler.get("canvas");
    const viewport = canvas.viewbox();
    if (!viewport?.scale) return;
    const starts = modeler.get("elementRegistry").getAll().filter((element: any) =>
        element.type === "bpmn:StartEvent" && typeof element.x === "number"
    );
    const leftmostStart = Math.min(...starts.map((element: any) => (element.x - viewport.x) * viewport.scale));
    const overlap = paletteRight + 16 - leftmostStart;
    if (starts.length && overlap > 0) canvas.viewbox({
        x: viewport.x - overlap / viewport.scale,
        y: viewport.y,
        width: viewport.width,
        height: viewport.height,
    });
};

const fitViewport = (modeler: any, container: HTMLDivElement | null) => {
    modeler.get("canvas").zoom("fit-viewport", "auto");
    if (container) revealStartBehindPalette(modeler, container);
};

interface FlussCanvasProps {
    xml: string;
    onChanged?: () => void;
    onSelectionChanged?: (selection: FlussElementSelection | null) => void;
    onElementsChanged?: (elements: any[]) => void;
    onReady?: (modeler: any) => void;
    validationElementIds?: string[];
    readOnly?: boolean;
    canAuthorScript?: boolean;
    onOpenCallActivityTarget?: (binding: any) => void;
}

const FlussCanvas = forwardRef<FlussCanvasHandle, FlussCanvasProps>(({
    xml,
    onChanged,
    onSelectionChanged,
    onElementsChanged,
    onReady,
    validationElementIds = [],
    readOnly = false,
    canAuthorScript = false,
    onOpenCallActivityTarget,
}, ref) => {
    const { t } = useTranslation();
    const { warning } = useNotification();
    flussCanvasTranslate = t;
    setFlussPopupScriptPermission(canAuthorScript);
    setFlussSubProcessToggleTranslate(t);
    setFlussCallActivityOverlayTranslate(t);
    setFlussCallActivityOpenHandler(onOpenCallActivityTarget);
    flussBpmnTranslate = (template, replacements) => {
        const key = FLUSS_BPMN_LABEL_KEYS[template];
        return key ? t(key, { defaultValue: template, ...replacements }) : template;
    };

    const containerRef = useRef<HTMLDivElement | null>(null);
    const modelerRef = useRef<any>(null);
    const importingRef = useRef(false);
    const markerIdsRef = useRef<string[]>([]);
    const focusMarkerIdRef = useRef<string | null>(null);
    const initialXmlRef = useRef(xml);
    const callbacksRef = useRef({ onChanged, onSelectionChanged, onElementsChanged, onReady });
    const notifyInvalidDropRef = useRef(() => {});
    callbacksRef.current = { onChanged, onSelectionChanged, onElementsChanged, onReady };
    notifyInvalidDropRef.current = () => warning(
        "FLUSS_CANVAS_INVALID_DROP_TARGET",
        t("fluss.canvas.invalid_drop_target", {
            defaultValue: "Drop the element exactly on the edge of the target node.",
        })
    );

    useImperativeHandle(ref, () => ({
        saveXml: async () => {
            const result = await modelerRef.current?.saveXML({ format: true });
            return result?.xml || "";
        },
        undo: () => modelerRef.current?.get("commandStack")?.undo(),
        redo: () => modelerRef.current?.get("commandStack")?.redo(),
        focusElement: (elementId: string) => {
            const modeler = modelerRef.current;
            const element = modeler?.get("elementRegistry")?.get(elementId);
            if (!element) return;
            const canvas = modeler.get("canvas");
            const registry = modeler.get("elementRegistry");
            const selection = modeler.get("selection");
            if (focusMarkerIdRef.current) {
                safeRemoveMarker(canvas, registry, focusMarkerIdRef.current, "fluss-validation-focus");
            }
            fitViewport(modeler, containerRef.current);
            canvas.scrollToElement(element);
            selection.select(element);
            canvas.addMarker(elementId, "fluss-validation-focus");
            focusMarkerIdRef.current = elementId;
        },
        clearMarkers: () => {
            const modeler = modelerRef.current;
            const canvas = modeler?.get("canvas");
            const registry = modeler?.get("elementRegistry");
            markerIdsRef.current.forEach((id) => safeRemoveMarker(canvas, registry, id, "fluss-validation-error"));
            markerIdsRef.current = [];
        },
        getModeler: () => modelerRef.current,
    }), []);

    useEffect(() => {
        if (!containerRef.current) return;

        let destroyed = false;
        const modeler = new Modeler({
            container: containerRef.current,
            keyboard: { bindTo: containerRef.current },
            additionalModules: [
                restrictedPaletteModule,
                flussTranslateModule,
                CreateAppendAnythingModule,
                flussPopupRankModule,
                flussContextPadModule,
                flussSubProcessToggleModule,
                flussCallActivityOverlayModule,
                flussElementReplaceModule,
            ],
            moddleExtensions: { fluss: flussModdleDescriptor },
        });
        modelerRef.current = modeler;
        const destroyModeler = () => {
            try {
                modeler.destroy();
            } catch {
                // bpmn-js may touch DOM nodes React already removed.
            }
        };

        const eventBus = modeler.get("eventBus");
        eventBus.on("commandStack.changed", () => {
            if (destroyed) return;
            if (!importingRef.current) callbacksRef.current.onChanged?.();
        });
        eventBus.on("selection.changed", (event: any) => {
            if (destroyed) return;
            const element = event.newSelection?.[0];
            callbacksRef.current.onSelectionChanged?.(
                element
                    ? {
                        id: element.id,
                        type: element.type,
                        businessObject: element.businessObject,
                        element,
                    } as FlussElementSelection
                    : null
            );
        });
        eventBus.on("elements.changed", (event: any) => {
            if (destroyed) return;
            callbacksRef.current.onElementsChanged?.(event.elements || []);
        });

        eventBus.on("create.end", (event: any) => {
            if (destroyed) return;
            const context = event?.context;
            if (context?.canExecute === false || !context?.target) notifyInvalidDropRef.current();
        });

        importingRef.current = true;
        const importPromise = modeler.importXML(ensureFlussDiagram(initialXmlRef.current))
            .then(() => {
                if (destroyed) return;
                fitViewport(modeler, containerRef.current);
                callbacksRef.current.onReady?.(modeler);
            })
            .catch(() => {
                // Avoid unhandled rejections after unmount; import errors have no UI surface here.
            })
            .finally(() => {
                if (!destroyed) importingRef.current = false;
            });

        return () => {
            destroyed = true;
            try {
                modeler.get("keyboard")?.unbind?.();
            } catch {
                // Best-effort keyboard teardown; full destroy is delayed until import settles.
            }
            importPromise.finally(destroyModeler);
            setFlussCallActivityOpenHandler(undefined);
            if (modelerRef.current === modeler) modelerRef.current = null;
        };
    }, []);

    useEffect(() => {
        const modeler = modelerRef.current;
        if (!modeler) return;
        const canvas = modeler.get("canvas");
        const registry = modeler.get("elementRegistry");
        markerIdsRef.current.forEach((id) => safeRemoveMarker(canvas, registry, id, "fluss-validation-error"));
        validationElementIds.forEach((id) => {
            if (registry.get(id)) canvas.addMarker(id, "fluss-validation-error");
        });
        markerIdsRef.current = validationElementIds;
    }, [validationElementIds]);

    useEffect(() => {
        const element = containerRef.current;
        if (!element) return;
        let lastZoomAt = 0;
        const onWheel = (event: WheelEvent) => {
            if (!event.ctrlKey) return;
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
            if (event.deltaY === 0) return;
            const now = Date.now();
            if (now - lastZoomAt < 55) return;
            lastZoomAt = now;
            const canvas = modelerRef.current?.get("canvas");
            if (!canvas) return;
            const rect = element.getBoundingClientRect();
            const current = canvas.zoom();
            canvas.zoom(Math.max(0.2, Math.min(4, current * (event.deltaY < 0 ? 1.1 : 1 / 1.1))), {
                x: event.clientX - rect.left, y: event.clientY - rect.top,
            });
        };
        element.addEventListener("wheel", onWheel, { passive: false, capture: true });
        return () => element.removeEventListener("wheel", onWheel, true);
    }, []);

    const zoom = (value: number | "fit-viewport") => {
        const canvas = modelerRef.current?.get("canvas");
        if (canvas) canvas.zoom(value, value === "fit-viewport" ? "auto" : undefined);
    };

    const controls = [
        { key: "in", label: t("fluss.canvas.zoom_in", { defaultValue: "Ampliar" }), action: () => zoom(Math.min(4, (modelerRef.current?.get("canvas")?.zoom() || 1) * 1.2)), symbol: "+" },
        { key: "out", label: t("fluss.canvas.zoom_out", { defaultValue: "Reduzir" }), action: () => zoom(Math.max(0.2, (modelerRef.current?.get("canvas")?.zoom() || 1) / 1.2)), symbol: "−" },
        { key: "fit", label: t("fluss.canvas.zoom_fit", { defaultValue: "Ajustar à tela" }), action: () => zoom("fit-viewport"), symbol: "⌗" },
        { key: "actual", label: t("fluss.canvas.zoom_actual", { defaultValue: "100%" }), action: () => zoom(1), symbol: "1:1" },
    ];

    return <Box sx={{ position: "relative", width: "100%", height: "100%" }}><Box
        ref={containerRef}
        title={readOnly ? undefined : t("fluss.canvas.intermediate_event_hint", { defaultValue: "Choose the intermediate event, then click the canvas to place it." })}
        tabIndex={readOnly ? -1 : 0}
        onPointerDown={(event) => {
            if (readOnly || (event.target as HTMLElement).closest("input, textarea, [contenteditable='true']")) return;
            containerRef.current?.focus({ preventScroll: true });
        }}
        className={`fluss-canvas${readOnly ? " fluss-canvas--readonly" : ""}`}
    /><Stack direction="column" spacing={0.25} sx={{ position: "absolute", right: 12, bottom: 12, zIndex: 5, bgcolor: "background.paper", borderRadius: 1, boxShadow: 2 }}>
        {controls.map((control) => <Tooltip key={control.key} title={control.label}><IconButton size="small" aria-label={control.label} onClick={control.action}>{control.symbol}</IconButton></Tooltip>)}
    </Stack></Box>;
});

FlussCanvas.displayName = "FlussCanvas";

export default FlussCanvas;
